import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { GoogleLogo, Eye, EyeSlash } from "@phosphor-icons/react";
import PageMeta from "@/components/PageMeta";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  /* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
  const googleLogin = () => {
    // Stash `next` so /auth/callback can resume the flow (e.g. bounce
    // back into /import?job=X&auto_commit=1 after a Google OAuth trip).
    if (next && next !== "/dashboard") {
      try { sessionStorage.setItem("rf_pending_next", next); } catch { /* ignore */ }
    }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center bg-paper">
      <PageMeta title="Sign in" description="Sign in to Recipe Filer to access your cookbooks and recipes." noindex />
      <div className="max-w-md mx-auto w-full px-6 py-16">
        <p className="overline mb-3">Welcome back</p>
        <h1 className="font-display text-4xl md:text-5xl font-black text-rf-ink mb-8 leading-tight">Sign in to your<br />cookbook shelf.</h1>

        <button onClick={googleLogin} data-testid="google-login-btn" className="btn-outline-ink w-full mb-2">
          <GoogleLogo size={20} weight="duotone" /> Continue with Google
        </button>
        <p className="text-xs text-rf-ink-soft text-center mb-5 leading-relaxed" data-testid="google-consent-note">
          Google may say <span className="font-bold">You&apos;ll see a one-time <span className="font-bold">Emergent</span> consent screen — that&apos;s just our sign-in partner. Your data stays with Recipe Filer only.ldquo;to continue to emergentagent.comYou&apos;ll see a one-time <span className="font-bold">Emergent</span> consent screen — that&apos;s just our sign-in partner. Your data stays with Recipe Filer only.rdquo;</span> — thatYou&apos;ll see a one-time <span className="font-bold">Emergent</span> consent screen — that&apos;s just our sign-in partner. Your data stays with Recipe Filer only.apos;s our secure sign-in partner. Pick your account and youYou&apos;ll see a one-time <span className="font-bold">Emergent</span> consent screen — that&apos;s just our sign-in partner. Your data stays with Recipe Filer only.apos;ll come straight back to RecipeFiler.com. Your recipes stay with Recipe Filer only.
        </p>
        <div className="flex items-center gap-3 my-6 text-xs text-rf-ink-soft uppercase tracking-widest">
          <div className="flex-1 h-px bg-rf-ink/10" /> or <div className="flex-1 h-px bg-rf-ink/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline">Email</label>
            <input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-rf mt-1" />
          </div>
          <div>
            <label className="overline">Password</label>
            <div className="relative mt-1">
              <input
                data-testid="login-password"
                required
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-rf pr-11 w-full"
              />
              <button
                type="button"
                data-testid="login-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-rf-ink-soft hover:text-rf-orange"
              >
                {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
              </button>
            </div>
            <div className="text-right mt-2">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-bold text-rf-orange hover:underline">Forgot password?</Link>
            </div>
          </div>
          <button data-testid="login-submit" disabled={loading} className="btn-amber w-full">{loading ? "Signing in…" : "Sign in"}</button>
        </form>

        <p className="mt-8 text-sm text-rf-ink-soft text-center">
          New here? <Link
            to={next !== "/dashboard" ? `/register?next=${encodeURIComponent(next)}` : "/register"}
            className="font-bold text-rf-orange">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
